import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Typography,
} from '@mui/material';
import {
  QuestionMultiChoice,
  DigitalSubmissionMultiChoice,
  PartialBy,
  QuestionTypeV2,
} from '../../../redux/types';

export type Props ={
  question: QuestionMultiChoice;
  sectionId?: number;
  enrollmentId?: number;
  caseId?: number;
  disabled?: boolean;
  summarizedView?: boolean;
  onSave?: (vals?: PartialBy<DigitalSubmissionMultiChoice, 'id'>) => Promise<void>;
  defaultSubmission?: DigitalSubmissionMultiChoice;
  deprecated?: boolean;
}

const MultiChoiceQuestionInput: React.FC<Props> = ({
  question,
  sectionId,
  enrollmentId,
  caseId,
  disabled,
  defaultSubmission,
  summarizedView,
  onSave,
  deprecated,
}: Props) => {
  const [saveOp, setSaveOp] = useState<'idle'|'saving'|'saved'>('idle');
  const [error, setError] = useState<string>();

  useEffect(() => {
    setSaveOp('idle');
    setError(undefined);
  }, [caseId, enrollmentId, sectionId]);

  const getDigitalSubmissionObj = (selectedAnswer: Record<number, boolean>): PartialBy<DigitalSubmissionMultiChoice, 'id'>|undefined => {
    if (sectionId === undefined || enrollmentId === undefined) {
      return undefined;
    }
    if (defaultSubmission) {
      return {
        ...defaultSubmission,
        answer: selectedAnswer,
      };
    }
    return {
      answer: selectedAnswer,
      digitalQuestionId: question.id,
      digitalSectionId: sectionId,
      enrollmentId,
      digitalCaseId: caseId,
      type: QuestionTypeV2.MultiChoice,
    };
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (saveOp === 'saving') {
      return;
    }
    setSaveOp('idle');
    const payload = {
      ...defaultSubmission?.answer,
      [Number(event.target.name)]: event.target.checked,
    };
    const submissionObj = getDigitalSubmissionObj(payload);
    if (onSave && submissionObj) {
      setSaveOp('saving');
      onSave(submissionObj)
        .then(() => {
          setSaveOp('saved');
        })
        .catch((e) => {
          setError(e.message);
          setSaveOp('idle');
        });
    }
  };

  const qPayload = summarizedView
    ? question.payload?.filter((p) => defaultSubmission?.answer[p.id])
    : question.payload;

  if (deprecated) {
    return (
      <Box>
        <Typography sx={{ textDecorationLine: 'line-through' }} color="grey.400">
          {question.text}
        </Typography>
        <Typography variant="caption">Deprecated</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl error={!!error} component="fieldset">
        <Typography fontWeight={500} gutterBottom>{question.text}</Typography>
        <FormGroup>
          {
            qPayload?.map((p) => (
              <FormControlLabel
                key={p.id}
                control={(
                  <Checkbox
                    checked={defaultSubmission?.answer[p.id] || false}
                    onChange={handleChange}
                    name={p.id.toString()}
                  />
                  )}
                label={p.text}
                disabled={disabled}
              />
            ))
          }
        </FormGroup>
        {
          !!error && (
            <FormHelperText>{error}</FormHelperText>
          )
        }
        {
          saveOp === 'saving' && (
            <FormHelperText>
              <FormattedMessage id="submission.saving" />
            </FormHelperText>
          )
        }
        {
          saveOp === 'saved' && (
            <FormHelperText>
              <Typography color="primary.main" variant="caption">
                <FormattedMessage id="submission.saved" />
              </Typography>
            </FormHelperText>
          )
        }
      </FormControl>
    </Box>
  );
};

export default MultiChoiceQuestionInput;
